import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Star,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Calendar
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KPIs = () => {
  const { organization } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  const periodLabels = {
    day: "Aujourd'hui",
    month: "Ce mois",
    year: "Cette année",
    all: "Tout"
  };

  useEffect(() => {
    if (organization) {
      fetchKPIs();
    } else {
      setLoading(false);
    }
  }, [organization, period]);

  const fetchKPIs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/kpis?period=${period}`);
      setKpis(response.data);
    } catch (error) {
      console.error("Error fetching KPIs:", error);
      toast.error("Erreur lors du chargement des KPIs");
    } finally {
      setLoading(false);
    }
  };

  // No organization screen (user not onboarded)
  if (!organization) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="kpis-page-no-organization">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Dashboard KPI
          </h1>
          <p className="text-slate-500 mt-1">
            Analysez vos performances en détail
          </p>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-8 border border-violet-100 text-center">
          <Target className="w-12 h-12 text-violet-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Configurez votre établissement
          </h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Complétez l'onboarding pour accéder à vos KPIs et analyses détaillées.
          </p>
          <Link to="/onboarding">
            <Button className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90" data-testid="complete-onboarding">
              Compléter l'onboarding
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const pieData = [
    { name: "Positifs", value: kpis?.positive_reviews || 0, color: "#10b981" },
    { name: "Négatifs", value: kpis?.negative_reviews || 0, color: "#f59e0b" }
  ];

  const hasEvolutionData = kpis?.initial_google_rating !== null && kpis?.initial_google_rating !== undefined;

  const stats = [
    {
      label: "Avis positifs",
      value: kpis?.positive_reviews || 0,
      icon: TrendingUp,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50",
      description: "≥ 4 étoiles"
    },
    {
      label: "Avis négatifs évités",
      value: kpis?.negative_reviews || 0,
      icon: TrendingDown,
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      description: "Interceptés avant Google"
    },
    {
      label: "Total avis collectés",
      value: kpis?.total_reviews || 0,
      icon: BarChart3,
      color: "text-violet-500",
      bgColor: "bg-violet-50",
      description: "Via votre QR code"
    },
    {
      label: "Note moyenne",
      value: kpis?.average_rating?.toFixed(1) || "0.0",
      icon: Star,
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      description: "Sur 5 étoiles"
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kpis-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
          Dashboard KPI
        </h1>
        <p className="text-slate-500">
          Analysez vos performances en détail
        </p>
      </div>

      {/* Period Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500 mr-2">
          <Calendar className="w-4 h-4" />
          <span>Période :</span>
        </div>
        {Object.entries(periodLabels).map(([key, label]) => (
          <Button
            key={key}
            variant={period === key ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(key)}
            className={`rounded-full ${
              period === key 
                ? "bg-violet-600 hover:bg-violet-700" 
                : "hover:bg-slate-100"
            }`}
            data-testid={`filter-${key}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Evolution Card - Show only if initial data exists */}
      {hasEvolutionData && (
        <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-6 text-white" data-testid="evolution-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Évolution de votre e-réputation
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm text-white/70 mb-1">Note Google initiale</p>
              <p className="text-2xl font-bold">{kpis?.initial_google_rating?.toFixed(1)}</p>
              <p className="text-xs text-white/50">{kpis?.initial_review_count || 0} avis</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm text-white/70 mb-1">Note actuelle</p>
              <p className="text-2xl font-bold">{kpis?.average_rating?.toFixed(1) || "0.0"}</p>
              <p className="text-xs text-white/50">{kpis?.total_reviews || 0} avis collectés</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm text-white/70 mb-1">Évolution</p>
              <div className="flex items-center gap-2">
                {kpis?.rating_evolution >= 0 ? (
                  <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-red-400" />
                )}
                <p className={`text-2xl font-bold ${kpis?.rating_evolution >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {kpis?.rating_evolution >= 0 ? '+' : ''}{kpis?.rating_evolution?.toFixed(2) || '0.00'}
                </p>
              </div>
              <p className="text-xs text-white/50">points</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm text-white/70 mb-1">Objectif</p>
              <p className="text-2xl font-bold">5.0</p>
              <p className="text-xs text-white/50">
                {(5 - (kpis?.average_rating || 0)).toFixed(1)} pts restants
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white rounded-2xl p-6 border border-slate-100"
            data-testid={`kpi-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center mb-4`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
            <p className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {stat.value}
            </p>
            <p className="text-sm font-medium text-slate-700 mt-1">{stat.label}</p>
            {stat.description && (
              <p className="text-xs text-slate-400 mt-0.5">{stat.description}</p>
            )}
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Bar Chart - Monthly Reviews */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="font-semibold text-slate-900 mb-4">Évolution mensuelle</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kpis?.monthly_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: "12px", 
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                  }} 
                />
                <Bar dataKey="positive" name="Positifs" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="negative" name="Négatifs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart - Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="font-semibold text-slate-900 mb-4">Répartition</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {pieData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-slate-600">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-6 border border-violet-100">
        <h3 className="font-semibold text-slate-900 mb-4">Impact Responsia sur votre e-réputation</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-2xl font-bold text-orange-600">
              {kpis?.negative_reviews || 0}
            </p>
            <p className="text-sm text-slate-600">Avis négatifs interceptés</p>
            <p className="text-xs text-slate-400 mt-1">Non publiés sur Google</p>
          </div>
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-2xl font-bold text-emerald-600">
              {kpis?.total_reviews > 0 
                ? (kpis?.average_rating - (
                    (kpis?.positive_reviews * 4.5 + kpis?.negative_reviews * 2) / kpis?.total_reviews
                  )).toFixed(1)
                : "0.0"
              }
            </p>
            <p className="text-sm text-slate-600">Points de note protégés</p>
            <p className="text-xs text-slate-400 mt-1">Grâce au filtrage</p>
          </div>
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-2xl font-bold text-violet-600">
              {kpis?.positive_reviews || 0}
            </p>
            <p className="text-sm text-slate-600">Avis positifs sur Google</p>
            <p className="text-xs text-slate-400 mt-1">Visibilité améliorée</p>
          </div>
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-2xl font-bold text-blue-600">
              {((kpis?.positive_reviews / kpis?.total_reviews) * 100 || 0).toFixed(0)}%
            </p>
            <p className="text-sm text-slate-600">Taux de satisfaction affiché</p>
            <p className="text-xs text-slate-400 mt-1">Sur Google</p>
          </div>
        </div>
      </div>

      {/* Call to Action for Action Plan */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900 mb-1">Plan d'action IA</h3>
            <p className="text-sm text-slate-500">
              L'IA analyse vos avis négatifs et génère des recommandations concrètes pour améliorer votre établissement.
            </p>
          </div>
          <Link to="/action-plan">
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="view-action-plan">
              Voir le plan d'action
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KPIs;
